/**
 * TanStack Query v5 hook-ovi za obligation form feature.
 * Omotava obligationService i proposer podatke; koristi useQueryClient za invalidaciju.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as obligationService from './obligationService';
import { searchDraftObligationById } from '@/shared/utils/obligationDraftSearch';
import CSOProfileService from '@/shared/services/cso/CSOProfileService';
import PublicBodyService from '@/shared/services/cso/PublicBodyService';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/** Query key factory za obligation form. @type {{ obligation: (id: string|number) => string[], obligations: () => string[], proposers: () => string[], relatedOrganizations: (obligationId: string|number) => string[], resolveActionPlan: (obligationId: string|number) => string[] }} */
export const obligationFormKeys = {
  obligation: (id) => ['obligation', id],
  obligations: () => ['obligations'],
  proposers: () => ['proposers'],
  proposerInstitutions: () => ['proposers', 'institutions'],
  relatedOrganizations: (obligationId) => ['obligation', obligationId, 'relatedOrganizations'],
  resolveActionPlan: (obligationId) => ['obligation', 'resolveActionPlan', obligationId],
};

const STALE_TIME_MS = 60 * 1000;

// ---------------------------------------------------------------------------
// useObligation(id, actionPlanId)
// ---------------------------------------------------------------------------

/**
 * Učitava jednu obavezu po ID-ju (search; zahteva actionPlanId za API).
 * @param {string|number} id - ID obaveze.
 * @param {string|number} [actionPlanId] - ID akcionog plana (potreban da query radi).
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne useQuery opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useObligation(id, actionPlanId, options = {}) {
  const query = useQuery({
    queryKey: [...obligationFormKeys.obligation(id), actionPlanId ?? null],
    queryFn: async () => {
      const draftObligation = await searchDraftObligationById(id, actionPlanId);
      if (draftObligation) return draftObligation;

      if (actionPlanId) {
        const result = await obligationService.searchObligations({
          action_plan_id: actionPlanId,
          id,
        });
        const list = result?.obligations ?? [];
        const found = list.find((o) => String(o.id) === String(id));
        if (found) return found;
      }

      return searchDraftObligationById(id, null);
    },
    enabled: !!id,
    staleTime: STALE_TIME_MS,
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    isFetching: query.isFetching,
    ...query,
  };
}

// ---------------------------------------------------------------------------
// useProposers()
// ---------------------------------------------------------------------------

/**
 * Učitava listu organizacija za izbor predlagača (OCD).
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne useQuery opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useProposers(options = {}) {
  const query = useQuery({
    queryKey: obligationFormKeys.proposers(),
    queryFn: () => CSOProfileService.getAllOrganizations(),
    staleTime: STALE_TIME_MS,
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    isFetching: query.isFetching,
    ...query,
  };
}

/**
 * Učitava institucije (javna tela) koje mogu biti predlagači obaveze.
 * @param {object} [options] - Dodatne useQuery opcije (npr. enabled).
 */
export function useProposerInstitutions(options = {}) {
  return useQuery({
    queryKey: obligationFormKeys.proposerInstitutions(),
    queryFn: () => PublicBodyService.getAllPublicBodies(),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

// ---------------------------------------------------------------------------
// useRelatedOrganizations(obligationId)
// ---------------------------------------------------------------------------

/**
 * Učitava povezane organizacije za obavezu.
 * @param {string|number} obligationId - ID obaveze.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne useQuery opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useRelatedOrganizations(obligationId, options = {}) {
  const query = useQuery({
    queryKey: obligationFormKeys.relatedOrganizations(obligationId),
    queryFn: () => obligationService.fetchRelatedOrganizations(obligationId),
    enabled: !!obligationId,
    staleTime: STALE_TIME_MS,
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    isFetching: query.isFetching,
    ...query,
  };
}

// ---------------------------------------------------------------------------
// useResolveObligationActionPlan(obligationId, actionPlanIds)
// ---------------------------------------------------------------------------

/**
 * Rešava koji akcioni plan sadrži datu obavezu (kada URL nema actionPlanId).
 * @param {string|number} obligationId - ID obaveze.
 * @param {Array<string|number>} actionPlanIds - Lista AP ID-jeva za pretragu.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne useQuery opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<{ actionPlanId: string|number }|null>}
 */
export function useResolveObligationActionPlan(obligationId, actionPlanIds, options = {}) {
  const ids = Array.isArray(actionPlanIds) ? actionPlanIds.filter(Boolean) : [];
  const query = useQuery({
    queryKey: obligationFormKeys.resolveActionPlan(obligationId),
    queryFn: async () => {
      for (const apId of ids) {
        try {
          const result = await obligationService.searchObligations({ action_plan_id: apId, id: obligationId });
          const list = result?.obligations ?? [];
          if (list.some((o) => String(o.id) === String(obligationId))) return { actionPlanId: apId };
        } catch {
          continue;
        }
      }
      return null;
    },
    enabled: !!obligationId && ids.length > 0,
    staleTime: STALE_TIME_MS,
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    ...query,
  };
}

// ---------------------------------------------------------------------------
// useCreateObligation()
// ---------------------------------------------------------------------------

/**
 * Mutation za kreiranje nove obaveze. Invalidira `['obligations']` na success.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useCreateObligation() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload) => obligationService.saveOrUpdateObligation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: obligationFormKeys.obligations() });
    },
  });

  return {
    data: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    ...mutation,
  };
}

// ---------------------------------------------------------------------------
// useUpdateObligation()
// ---------------------------------------------------------------------------

/**
 * Mutation za ažuriranje obaveze. Invalidira `['obligation', id]` na success.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useUpdateObligation() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload) => obligationService.saveOrUpdateObligation(payload),
    onSuccess: (_, variables) => {
      const id = variables?.id ?? variables?.obligationId;
      if (id != null) {
        queryClient.invalidateQueries({ queryKey: obligationFormKeys.obligation(id) });
      }
      queryClient.invalidateQueries({ queryKey: obligationFormKeys.obligations() });
    },
  });

  return {
    data: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    ...mutation,
  };
}
